import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { StudentFailedReason } from "@/features/training/model/Training/StudentFailedReason";
import { TrainingCourse } from "@/features/training/model/Training/TrainingCourse";
import { TrainingDrill } from "@/features/training/model/Training/TrainingDrill";
import {
	DrillSummary,
	TrainingDrillFolder,
} from "@/features/training/model/Training/TrainingDrillFolder";
import {
	TrainingDrillSession,
	TrainingDrillSessionSummary,
	TrainingDrillSessionSummaryWithKpiValue,
} from "@/features/training/model/Training/TrainingDrillSession";
import {
	TrainingCourseSummary,
	TrainingFolder,
} from "@/features/training/model/Training/TrainingFolder";
import {
	TrainingKpi,
	TrainingKpiStatisticsQueryOptions,
} from "@/features/training/model/Training/TrainingKpi";
import { TrainingKpiStatistic } from "@/features/training/model/Training/TrainingKpiStatistic";
import { TrainingLink } from "@/features/training/model/Training/TrainingLink";
import {
	TrainingSession,
	TrainingSessionSummary,
	TrainingSessionsQueryOptions,
} from "@/features/training/model/Training/TrainingSession";
import { useQuery } from "@tanstack/react-query";

import { UserSummary } from "@/model/User/UserSummary";

export interface Session {
	SessionId: number;
	SessionDate: string;
	CourseName: string;
	Reason: string;
}

export interface UserItem {
	UserId: number;
	Name: string;
	Sessions: Session[];
}

export interface TrainingUsersResponse {
	Items: UserItem[];
}

export const useFetchDrillFolders = () => {
	return useQuery<TrainingDrillFolder[]>({
		queryKey: [TrainingQueryKeys.drillFolders],
		queryFn: () => SATOSPortal.GetDrillFolders(),
	});
};

export const useFetchDrillFolder = (id: number) => {
	return useQuery<TrainingDrillFolder>({
		queryKey: [TrainingQueryKeys.drillFolders, id],
		queryFn: () => SATOSPortal.GetDrillFolder(id),
	});
};

export const useFetchCourseFolders = () => {
	return useQuery<TrainingFolder[]>({
		queryKey: [TrainingQueryKeys.courseFolders],
		queryFn: () => SATOSPortal.GetCourseFolders(),
	});
};

export const useFetchCourse = (courseId: number) => {
	return useQuery<TrainingCourse>({
		queryKey: [TrainingQueryKeys.courses, courseId],
		queryFn: () => SATOSPortal.GetCourse(courseId),
		enabled: !!courseId,
	});
};

export const useFetchCourses = () => {
	return useQuery<TrainingCourseSummary[]>({
		queryKey: [TrainingQueryKeys.courses],
		queryFn: () => SATOSPortal.GetCourses(),
	});
};

export const useFetchDrill = (drillId: number | undefined) => {
	return useQuery<TrainingDrill>({
		queryKey: [TrainingQueryKeys.drills, drillId],
		queryFn: () => SATOSPortal.GetDrill(drillId),
		enabled: !!drillId,
	});
};

export const useFetchDrills = () => {
	return useQuery<DrillSummary[]>({
		queryKey: [TrainingQueryKeys.drills],
		queryFn: () => SATOSPortal.GetDrills(),
	});
};

export const useFetchDrillSessions = (
	queryOptions: TrainingSessionsQueryOptions,
	isActive = false,
) => {
	return useQuery<TrainingDrillSessionSummary[]>({
		queryKey: [TrainingQueryKeys.drillSessions, queryOptions],
		queryFn: () => SATOSPortal.GetDrillSessions(queryOptions),
		enabled: isActive,
	});
};

export const useFetchDrillSession = (sessionId: number) => {
	return useQuery<TrainingDrillSession>({
		queryKey: [TrainingQueryKeys.drillSessions, sessionId],
		queryFn: () => SATOSPortal.GetDrillSession(sessionId),
		enabled: !!sessionId,
	});
};

export const useFetchActiveSessionsByDrillId = (
	drillId: number,
	fetch: boolean,
) => {
	return useQuery<TrainingDrillSession[]>({
		queryKey: [
			TrainingQueryKeys.drills,
			drillId,
			TrainingQueryKeys.activeDrillSessions,
		],
		queryFn: () => SATOSPortal.GetActiveSessionsByDrillId(drillId),
		enabled: fetch,
	});
};

export const useFetchArchivedSessionsByDrillId = (
	drillId: number,
	fetch: boolean,
) => {
	return useQuery<TrainingDrillSession[]>({
		queryKey: [
			TrainingQueryKeys.drills,
			drillId,
			TrainingQueryKeys.archivedDrillSessions,
		],
		queryFn: () => SATOSPortal.GetArchivedSessionsByDrillId(drillId),
		enabled: fetch,
	});
};

export const useFetchCourseSessions = (
	queryOptions: TrainingSessionsQueryOptions,
	isActive = false,
) => {
	return useQuery<TrainingSessionSummary[]>({
		queryKey: [TrainingQueryKeys.courseSessions, queryOptions],
		queryFn: () => SATOSPortal.GetCourseSessions(queryOptions),
		enabled: isActive,
	});
};

export const useFetchCourseSession = (sessionId: number) => {
	return useQuery<TrainingSession>({
		queryKey: [TrainingQueryKeys.courseSessions, sessionId],
		queryFn: () => SATOSPortal.GetCourseSession(sessionId),
		enabled: !!sessionId,
	});
};

export const useFetchActiveSessionsByCourseId = (
	courseId: number,
	fetch: boolean,
) => {
	return useQuery<TrainingSessionSummary[]>({
		queryKey: [
			TrainingQueryKeys.courses,
			courseId,
			TrainingQueryKeys.activeCourseSessions,
		],
		queryFn: () => SATOSPortal.GetActiveSessionsByCourseId(courseId),
		enabled: fetch,
	});
};

export const useFetchArchivedSessionsByCourseId = (
	courseId: number,
	fetch: boolean,
) => {
	return useQuery<TrainingSessionSummary[]>({
		queryKey: [
			TrainingQueryKeys.courses,
			courseId,
			TrainingQueryKeys.archivedCourseSessions,
		],
		queryFn: () => SATOSPortal.GetArchivedSessionsByCourseId(courseId),
		enabled: fetch,
	});
};

export const useFetchDrillSessionsByKpiId = (
	kpiId: number,
	queryOptions: TrainingKpiStatisticsQueryOptions,
) => {
	return useQuery<TrainingDrillSessionSummaryWithKpiValue[]>({
		queryKey: [
			TrainingQueryKeys.kpis,
			kpiId,
			TrainingQueryKeys.drillSessions,
			queryOptions,
		],
		queryFn: () => SATOSPortal.GetDrillSessionsByKpiId(kpiId, queryOptions),
	});
};

export const useFetchTrainingLink = (linkId: number) => {
	return useQuery<TrainingLink>({
		queryKey: [TrainingQueryKeys.links, linkId],
		queryFn: () => SATOSPortal.GetTrainingLink(linkId),
	});
};

export const useFetchUserSummariesByCurrentCompany = () => {
	return useQuery<UserSummary[]>({
		queryKey: [TrainingQueryKeys.participants],
		queryFn: () => SATOSPortal.GetUserSummariesByCurrentCompany(),
		staleTime: 3000,
	});
};

export const useFetchStudentFailedReasons = () => {
	return useQuery<StudentFailedReason[]>({
		queryKey: [TrainingQueryKeys.studentFailedReasons],
		queryFn: () => SATOSPortal.GetStudentFailedReasons(),
	});
};

export const useFetchTrainingKpis = () => {
	return useQuery<TrainingKpi[]>({
		queryKey: [TrainingQueryKeys.kpis],
		queryFn: () => SATOSPortal.GetKpis(),
	});
};

export const useFetchTrainingUsersWithMissedSessions = () => {
	return useQuery<TrainingUsersResponse, Error>({
		queryKey: [TrainingQueryKeys.participantsWithMissedSessions],
		queryFn: () => SATOSPortal.GetTrainingUsersWithMissedSessions(),
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: false,
	});
};

export const useFetchTrainingKpi = (kpiId: number) => {
	return useQuery<TrainingKpi>({
		queryKey: [TrainingQueryKeys.kpis, kpiId],
		queryFn: () => SATOSPortal.GetTrainingKpi(kpiId),
	});
};

export const useFetchKpiStatistics = (
	queryOptions: TrainingKpiStatisticsQueryOptions,
) => {
	return useQuery<TrainingKpiStatistic[]>({
		queryKey: [TrainingQueryKeys.kpiStatistics, queryOptions],
		queryFn: () => SATOSPortal.GetTrainingKpiStatistics(queryOptions),
	});
};

export class TrainingQueryKeys {
	static readonly activeCourseSessions = "activeCourseSessions";
	static readonly archivedCourseSessions = "archivedCourseSessions";
	static readonly activeDrillSessions = "activeDrillSessions";
	static readonly archivedDrillSessions = "archivedDrillSessions";
	static readonly courseFolders = "courseFolders";
	static readonly courses = "courses";
	static readonly courseSessions = "courseSessions";
	static readonly drillFolders = "drillFolders";
	static readonly drills = "drills";
	static readonly drillSessions = "drillSessions";
	static readonly files = "files";
	static readonly kpis = "kpis";
	static readonly kpiStatistics = "kpiStatistics";
	static readonly links = "links";
	static readonly participants = "participants";
	static readonly participantsWithMissedSessions =
		"participantsWithMissedSessions";
	static readonly studentFailedReasons = "studentFailedReasons";
}
